import { useCallback, useEffect, useRef } from 'react';

export interface UseIdleTimerOptions
{
	/** Full inactivity threshold before timeout fires (ms). */
	idleThresholdMs: number;
	/** How long before timeout to fire the warning (ms). Must be <= idleThresholdMs. */
	warningWindowMs: number;
	/** Fires when the warning phase begins. */
	onWarn: () => void;
	/** Fires when the full idle threshold has elapsed. */
	onTimeout: () => void;
	/** When false, no listeners or timers are wired up. */
	enabled: boolean;
}

export interface UseIdleTimerReturn
{
	/** Clears warning state and restarts the idle countdown from zero. */
	reset: () => void;
}

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
	'mousedown',
	'keydown',
	'touchstart',
	'scroll',
	'pointerdown',
	'wheel',
];

/**
 * Watches window-level user activity and fires `onWarn` after
 * `idleThresholdMs - warningWindowMs` of inactivity, then `onTimeout`
 * after the full `idleThresholdMs`. Activity during the warning phase
 * does NOT reset the countdown — the caller must invoke the returned
 * `reset()` (e.g. from a "Stay" click in a modal).
 */
export function useIdleTimer(options: UseIdleTimerOptions): UseIdleTimerReturn
{
	const { idleThresholdMs, warningWindowMs, onWarn, onTimeout, enabled } = options;

	// Stable refs for callbacks so we can safely re-schedule without re-binding listeners.
	const onWarnRef = useRef(onWarn);
	const onTimeoutRef = useRef(onTimeout);

	useEffect(() =>
	{
		onWarnRef.current = onWarn;
	}, [onWarn]);

	useEffect(() =>
	{
		onTimeoutRef.current = onTimeout;
	}, [onTimeout]);

	// Timer + phase refs — kept out of state so they don't trigger renders.
	const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const inWarningPhaseRef = useRef(false);

	const clearTimers = useCallback(() =>
	{
		if (warnTimerRef.current !== null)
		{
			clearTimeout(warnTimerRef.current);
			warnTimerRef.current = null;
		}
		if (timeoutTimerRef.current !== null)
		{
			clearTimeout(timeoutTimerRef.current);
			timeoutTimerRef.current = null;
		}
	}, []);

	const scheduleTimers = useCallback(() =>
	{
		clearTimers();
		const warnDelay = Math.max(0, idleThresholdMs - warningWindowMs);
		warnTimerRef.current = setTimeout(() =>
		{
			inWarningPhaseRef.current = true;
			onWarnRef.current();
		}, warnDelay);
		timeoutTimerRef.current = setTimeout(() =>
		{
			inWarningPhaseRef.current = false;
			onTimeoutRef.current();
		}, idleThresholdMs);
	}, [idleThresholdMs, warningWindowMs, clearTimers]);

	// External reset — clears warning phase and restarts the countdown.
	const reset = useCallback(() =>
	{
		if (!enabled)
		{
			return;
		}
		inWarningPhaseRef.current = false;
		scheduleTimers();
	}, [enabled, scheduleTimers]);

	useEffect(() =>
	{
		if (!enabled)
		{
			clearTimers();
			inWarningPhaseRef.current = false;
			return;
		}

		const handleActivity = () =>
		{
			// Once we've entered the warning phase, only an explicit reset() call
			// (e.g. from the modal's "Stay" button) restarts the countdown.
			if (inWarningPhaseRef.current)
			{
				return;
			}
			scheduleTimers();
		};

		// visibilitychange lives on document, not window — and we only care about
		// the visible transition. A hidden->visible flip counts as "user came back
		// to the tab" and should reset the idle countdown (subject to the same
		// warning-phase guard as normal activity).
		const handleVisibility = () =>
		{
			if (document.visibilityState !== 'visible')
			{
				return;
			}
			handleActivity();
		};

		for (const evt of ACTIVITY_EVENTS)
		{
			window.addEventListener(evt, handleActivity, { passive: true });
		}
		document.addEventListener('visibilitychange', handleVisibility);
		scheduleTimers();

		return () =>
		{
			for (const evt of ACTIVITY_EVENTS)
			{
				window.removeEventListener(evt, handleActivity);
			}
			document.removeEventListener('visibilitychange', handleVisibility);
			clearTimers();
			inWarningPhaseRef.current = false;
		};
	}, [enabled, scheduleTimers, clearTimers]);

	return { reset };
}

export default useIdleTimer;
